import React, { useEffect, useState, useRef } from "react";
import { Box, Button } from "@mui/material";
import { toast } from "react-toastify";
import { Category, fetchCategories } from "../../app/services/CategoryService";
import {
  changeStatus,
  deleteSubCategory,
  fetchSubCategory,
  SubCategory,
  uploadSubCategory,
} from "../../app/services/subcatagoryservice";
import CreateTable from "../../app/shared/common/table/CreateTable";
import PageWrapper from "../../app/shared/common/PageWrapper";
import TableActions from "../../app/shared/common/table/TableAction";
import DropDown from "../../app/shared/common/DropDown";

const tableColumns = [
  { key: "code", label: "Code" },
  { key: "image", label: "Image" },
  { key: "name", label: "Name" },
  { key: "category.name", label: "Category" },
  { key: "is_active", label: "Status" },
];

const SubCategoryList: React.FC = () => {
  const [data, setData] = useState<SubCategory[]>([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [categoryFilter, setCategoryFilter] = useState<string>(""); // selected category code
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(
    null
  );

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const getCategories = async () => {
    try {
      setLoading(true);
      const res = await fetchCategories();
      if ((res as any).status === "success") {
        setCategories((res as any).data);
      } else {
        toast.error("Something went wrong");
      }
      setLoading(false);
    } catch (error) {
      toast.error("Failed to fetch categories");
      setLoading(false);
    }
  };

  const handleOpenDialog = (id: number) => {
    setSelectedCategoryId(id);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedCategoryId(null);
  };

  const handleConfirmDelete = async () => {
    if (!selectedCategoryId) return;

    try {
      await deleteSubCategory(selectedCategoryId);
      setData((prev) => prev.filter((cat) => cat.code !== selectedCategoryId));
      toast.success("Sub Category deleted successfully.");
    } catch (err) {
      toast.error("Something went wrong while deleting.");
    } finally {
      handleCloseDialog();
    }
  };

  const loadData = async () => {
    try {
      const response = await fetchSubCategory();
      const subcategories = response.data;
      setData(subcategories);
    } catch (error) {
      toast.error("Error fetching subcategory");
    }
  };

  const handleToggleStatus = async (code: number) => {
    try {
      await changeStatus(code);

      setData((prev) =>
        prev.map((p) =>
          p.code === code ? { ...p, is_active: !p.is_active } : p
        )
      );
      toast("Status changed successfully");
    } catch (error) {
      toast.error("Error updating product status");
    }
  };

  // ---- Upload Handling ----
  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    try {
      const res = await uploadSubCategory(formData);

      if (res.data.status === "success") {
        toast.success(res.data.message);
        await loadData(); // reload after upload
      } else {
        toast.error(res.data.message);
      }
    } catch (error) {
      toast.error("File upload failed");
    } finally {
      if (fileInputRef.current) {
        fileInputRef.current.value = ""; // reset input
      }
    }
  };

  useEffect(() => {
    getCategories();
    loadData();
  }, []);

  // ✅ Filter subcategories by category.code
  const filteredData = categoryFilter
    ? data.filter((item) => item.category?.code === Number(categoryFilter))
    : data;

  return (
    <PageWrapper
      title={"Sub Categories"}
      navigateTo={"/catalog/subcategory/add"}
      buttonTitle={"Add Sub-Category"}
      actionName={"sub category"}
      open={openDialog}
      onClose={handleCloseDialog}
      onConfirm={handleConfirmDelete}
      extraAction={
        <>
          <Button
            variant="contained"
            sx={{
              backgroundColor: "#fcb500",
              color: "#000",
              fontWeight: 600,
            }}
            onClick={handleUploadClick}
          >
            Upload Sub-Category
          </Button>
          <input
            type="file"
            accept=".xlsx"
            ref={fileInputRef}
            style={{ display: "none" }}
            onChange={handleFileChange}
          />
        </>
      }
    >
      <Box display="flex" gap={2} mb={2}>
        <DropDown
          label="Filter by Category"
          value={categoryFilter}
          onChange={setCategoryFilter}
          options={categories.map((c) => ({
            id: c.code, // ✅ category.code from API
            name: c.name,
          }))}
          allLabel="All Category"
        />
      </Box>

      <CreateTable
        columns={tableColumns}
        data={filteredData}
        loading={loading}
        onCheckboxClick={(row) => handleToggleStatus(row.code)} // ✅ use code instead of id
        renderActions={(subcat) => (
          <TableActions
            navigateTo={`/catalog/subcategory/update/${subcat.code}`}
            onClick={() => handleOpenDialog(subcat.code)}
          />
        )}
      />
    </PageWrapper>
  );
};

export default SubCategoryList;
